import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  Drawer,
  Tabs,
  Tab,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import Grid3x3Icon from '@mui/icons-material/Grid3x3';
import DevicesIcon from "@mui/icons-material/Devices";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SyncIcon from "@mui/icons-material/Sync";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TerminalIcon from "@mui/icons-material/Terminal";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import ipconfig, { accosyncIpConfig } from "../../Core/ipconfig";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";
import moment from "moment";
import { getSocket } from "../../Core/socketSetup";

import { formatPhoneNumber } from "../../Core/util/formatPhone";

interface UserInfo {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  countryCode?: string;
}

interface DeviceInfo {
  _id: string;
  deviceName: string;
  deviceModel: string;
  modelName: string;
  manufacturer: string;
  deviceOwnerEmail: string;
  deviceOwnerId: string;
  __v: number;
}

interface ConnectionData {
  socketId: string;
  userInfo: UserInfo;
  deviceInfo: DeviceInfo;
  connectedAt: string;
}

interface ModuleSyncStatus {
  status: boolean;
  startedAt: string;
  completedAt: string;
}

interface ModulesObject {
  [key: string]: ModuleSyncStatus;
}

interface CompanyIdObject {
  _id: string;
  tally_companyInfo: {
    Name: string;
  };
  dbName: string;
}

interface InitialSyncRecord {
  _id: string;
  fy: {
    start: string;
    end: string;
  };
  modules: ModulesObject;
  userId: string;
  companyId: CompanyIdObject;
  dbName: string;
  deviceId: string;
  status: string;
  createdAt: string;
  __v: number;
}

interface MissedEventRecord {
  _id: string;
  deviceId: string;
  userId: string;
  eventType: string;
  payload: {
    message: string;
    dbName: string;
    companyName: string;
    companyId: string;
    timestamp: string;
  };
  isCompleted: Boolean;
  status: string;
  isCompanyLevel: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface RequiredModuleConfig {
  moduleName: string;
  isFullSyncNeeded: boolean;
  storage: string;
  tableName: string | null;
}

interface IncrementalLogRecord {
  _id: string;
  deviceId: string;
  userId: string;
  companyName: string;
  companyId: string;
  dbName: string;
  syncStartTime: string;
  syncEndTime: string;
  moduleWiseSyncRecord: {
    [key: string]: {
      status: string;
      isFullSyncNeeded: boolean;
      startedAt: string;
      completedAt: string;
    };
  };
  status: string;
  lastVoucherAlterId: number;
  lastMasterAlterId: number;
  lastReportSynced: string;
  requiredModules: RequiredModuleConfig[];
  isFullMasterSync: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface SyncReportData {
  initialSyncs: InitialSyncRecord[];
  missedEvents: MissedEventRecord[];
  incrementalLogs: IncrementalLogRecord[];
}

// Animation styles for the pulsing active connection indicator
const pulseAnimation = {
  "@keyframes pulse": {
    "0%": {
      transform: "scale(0.95)",
      boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.7)",
    },
    "70%": {
      transform: "scale(1.1)",
      boxShadow: "0 0 0 8px rgba(76, 175, 80, 0)",
    },
    "100%": {
      transform: "scale(0.95)",
      boxShadow: "0 0 0 0 rgba(76, 175, 80, 0)",
    },
  },
};

export const SocketConnections = () => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [connections, setConnections] = useState<ConnectionData[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Tab & Pagination State
  const [currentTab, setCurrentTab] = useState<string>("live");
  const [allDevices, setAllDevices] = useState<ConnectionData[]>([]);
  const [allDevicesCount, setAllDevicesCount] = useState<number>(0);
  
  // Pagination State for All Tab
  const [allDevicesPage, setAllDevicesPage] = useState<number>(1);
  const [allDevicesLimit] = useState<number>(10); // 12 items fits a 3x4 or 4x3 card layout beautifully
  const [allDevicesTotalPages, setAllDevicesTotalPages] = useState<number>(1);
  const [allDevicesTotalItems, setAllDevicesTotalItems] = useState<number>(0);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

  // Enriched All Devices to detect active socket connection
  const enrichedAllDevices = useMemo(() => {
    return allDevices.map((device) => {
      const activeConn = connections.find(c => c.deviceInfo?._id === device.deviceInfo?._id);
      if (activeConn) {
        return {
          ...device,
          socketId: activeConn.socketId,
          connectedAt: activeConn.connectedAt,
          userInfo: activeConn.userInfo
        };
      }
      return device;
    });
  }, [allDevices, connections]);

  // Drawer / Sync Report State variables
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedConnection, setSelectedConnection] = useState<ConnectionData | null>(null);
  const [syncReport, setSyncReport] = useState<SyncReportData | null>(null);
  const [syncReportLoading, setSyncReportLoading] = useState<boolean>(false);
  const [drawerTab, setDrawerTab] = useState<number>(0);
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});

  // Drawer Tab Search states
  const [initialSyncSearch, setInitialSyncSearch] = useState<string>("");
  const [missedEventSearch, setMissedEventSearch] = useState<string>("");
  const [incrementalLogSearch, setIncrementalLogSearch] = useState<string>("");

  // Event Trigger Modal State variables
  const [triggerModalOpen, setTriggerModalOpen] = useState<boolean>(false);
  const [triggerConnection, setTriggerConnection] = useState<ConnectionData | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string>("REFRESH_COMPANY_LIST");
  const [payloadJson, setPayloadJson] = useState<string>(
    JSON.stringify({ msg: "REFRESH_COMPANY_LIST" }, null, 2)
  );
  const [triggerLoading, setTriggerLoading] = useState<boolean>(false);
  const [triggerResponse, setTriggerResponse] = useState<any>(null);
  const [triggerResponseStatus, setTriggerResponseStatus] = useState<number | null>(null);
  const [triggerResponseTime, setTriggerResponseTime] = useState<number | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleOpenTriggerModal = (conn: ConnectionData) => {
    setTriggerConnection(conn);
    setSelectedEvent("REFRESH_COMPANY_LIST");
    setPayloadJson(JSON.stringify({ msg: "REFRESH_COMPANY_LIST" }, null, 2));
    setTriggerResponse(null);
    setTriggerResponseStatus(null);
    setTriggerResponseTime(null);
    setJsonError(null);
    setTriggerModalOpen(true);
  };

  const handleEventChange = (eventName: string) => {
    setSelectedEvent(eventName);
    try {
      const defaultPayload = { msg: eventName };
      setPayloadJson(JSON.stringify(defaultPayload, null, 2));
      setJsonError(null);
    } catch (err) {
      // ignore
    }
  };

  const handlePayloadChange = (val: string) => {
    setPayloadJson(val);
    try {
      JSON.parse(val);
      setJsonError(null);
    } catch (err: any) {
      setJsonError(err.message || "Invalid JSON syntax");
    }
  };

  const handleSendTrigger = async () => {
    if (!triggerConnection) return;

    // Validate JSON before sending
    let parsedPayload = {};
    try {
      parsedPayload = JSON.parse(payloadJson);
    } catch (err: any) {
      setJsonError(err.message || "Invalid JSON syntax");
      return;
    }

    setTriggerLoading(true);
    setTriggerResponse(null);
    setTriggerResponseStatus(null);
    setTriggerResponseTime(null);

    const startTime = Date.now();
    try {
      const response = await axios.post(
        `${accosyncIpConfig}/v2/internal/event/trigger`,
        {
          socketId: triggerConnection.socketId,
          eventName: selectedEvent,
          payload: parsedPayload,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const duration = Date.now() - startTime;
      setTriggerResponseTime(duration);
      setTriggerResponseStatus(response.status);
      setTriggerResponse(response.data);
    } catch (error: any) {
      const duration = Date.now() - startTime;
      setTriggerResponseTime(duration);
      if (error.response) {
        setTriggerResponseStatus(error.response.status);
        setTriggerResponse(error.response.data);
      } else {
        setTriggerResponseStatus(500);
        setTriggerResponse({ message: error.message || "Network Error" });
      }
    } finally {
      setTriggerLoading(false);
    }
  };

  const fetchSyncReport = async (deviceId: string) => {
    if (!deviceId) return;
    setSyncReportLoading(true);
    setSyncReport(null);
    try {
      const response = await axios.get(`${ipconfig}/syncreport?id=${deviceId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      console.log(response.data.data, "response.data.data");

      if (response.data?.status) {
        setSyncReport(response.data.data || { initialSyncs: [], missedEvents: [], incrementalLogs: [] });
      }
    } catch (error) {
      console.error("Error fetching sync report:", error);
    } finally {
      setSyncReportLoading(false);
    }
  };

  const handleCardClick = (conn: ConnectionData) => {
    setSelectedConnection(conn);
    setDrawerOpen(true);
    setDrawerTab(0);
    setExpandedItems({});
    setInitialSyncSearch("");
    setMissedEventSearch("");
    setIncrementalLogSearch("");
    fetchSyncReport(conn.deviceInfo?._id);
  };

  const toggleExpand = (itemId: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const fetchAllDevices = async (page = 1, search = "", showSpinner = false) => {
    if (showSpinner) {
      setLoading(true);
    }
    try {
      const response = await axios.get(
        `${ipconfig}/devices?page=${page}&limit=${allDevicesLimit}&search=${encodeURIComponent(search)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.data?.status) {
        const fetchedData = response.data.data || [];
        const pagination = response.data.pagination || { totalItems: fetchedData.length, totalPages: 1, currentPage: 1 };
        setAllDevicesCount(pagination.totalItems);
        setAllDevicesTotalItems(pagination.totalItems);
        setAllDevicesTotalPages(pagination.totalPages);
        if (pagination.currentPage !== page) {
          setAllDevicesPage(pagination.currentPage);
        }

        const mapped: ConnectionData[] = fetchedData.map((item: any) => ({
          socketId: "",
          userInfo: {
            _id: item.deviceOwnerId?._id || item.deviceOwnerId || "",
            name: item.deviceOwnerId?.name || (item.deviceOwnerEmail ? item.deviceOwnerEmail.split("@")[0] : "N/A"),
            email: item.deviceOwnerEmail || item.deviceOwnerId?.userName || "N/A",
            phoneNumber: item.deviceOwnerId?.phoneNumber || "N/A",
            countryCode: item.deviceOwnerId?.countryCode || "",
          },
          deviceInfo: {
            _id: item._id,
            deviceName: item.deviceName,
            deviceModel: item.deviceModel,
            modelName: item.modelName,
            manufacturer: item.manufacturer,
            deviceOwnerEmail: item.deviceOwnerEmail,
            deviceOwnerId: item.deviceOwnerId?._id || item.deviceOwnerId,
            __v: item.__v || 0,
          },
          connectedAt: "",
        }));
        setAllDevices(mapped);
      }
    } catch (error) {
      console.error("Error fetching all registered devices:", error);
    } finally {
      if (showSpinner) {
        setLoading(false);
      }
    }
  };

  const fetchConnections = async (showSpinner: any = false) => {
    const shouldShowSpinner = showSpinner === true;
    if (shouldShowSpinner) {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${accosyncIpConfig}/v2/internal/connections`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data?.status) {
        const fetchedData = response.data.data || [];
        setConnections(fetchedData);
        setTotalCount(response.data.totalConnections || fetchedData.length);
      }
    } catch (error) {
      console.error("Error fetching socket connections:", error);
    } finally {
      if (shouldShowSpinner) {
        setLoading(false);
      }
    }
  };

  const handleRefreshAll = async (showSpinner: boolean = false, keepState: boolean = false) => {
    if (showSpinner) {
      setLoading(true);
    }
    const promises: Promise<any>[] = [fetchConnections(false)];
    
    // Only refresh registered devices if on All tab, keeping current page/search if keepState is true
    if (currentTab === "all") {
      const pageToFetch = keepState ? allDevicesPage : 1;
      const searchToFetch = keepState ? debouncedSearchValue : "";
      promises.push(fetchAllDevices(pageToFetch, searchToFetch, false));
    }
    
    await Promise.all(promises);
    if (showSpinner) {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch of connections on mount
    fetchConnections(true);

    const handleRefresh = () => {
      // Socket event only updates live connections list
      fetchConnections(false);
    };

    const socket = getSocket();
    if (socket) {
      socket.on("refresh_connection", handleRefresh);
    }

    return () => {
      if (socket) {
        socket.off("refresh_connection", handleRefresh);
      }
    };
  }, []);

  // Debounce search value for "All" tab backend-driven search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  // Trigger backend-driven devices fetch when page, tab, or debounced search changes
  useEffect(() => {
    if (currentTab === "all") {
      fetchAllDevices(allDevicesPage, debouncedSearchValue, true);
    }
  }, [allDevicesPage, debouncedSearchValue, currentTab]);



  // Filter connections based on search value using useMemo
  const filteredConnections = useMemo(() => {
    if (!searchValue.trim()) {
      return connections;
    }

    const query = searchValue.toLowerCase();
    return connections.filter((conn) => {
      const name = conn.userInfo?.name?.toLowerCase() || "";
      const email = conn.userInfo?.email?.toLowerCase() || "";
      const phone = conn.userInfo?.phoneNumber || "";
      const socketId = conn.socketId?.toLowerCase() || "";
      const userId = conn.userInfo?._id?.toLowerCase() || "";
      const deviceId = conn.deviceInfo?._id?.toLowerCase() || "";
      const deviceName = conn.deviceInfo?.deviceName?.toLowerCase() || "";
      const manufacturer = conn.deviceInfo?.manufacturer?.toLowerCase() || "";
      const modelName = conn.deviceInfo?.modelName?.toLowerCase() || "";

      return (
        name.includes(query) ||
        email.includes(query) ||
        phone.includes(query) ||
        socketId.includes(query) ||
        userId.includes(query) ||
        deviceId.includes(query) ||
        deviceName.includes(query) ||
        manufacturer.includes(query) ||
        modelName.includes(query)
      );
    });
  }, [searchValue, connections]);

  // Filter all devices using enrichedAllDevices (pre-filtered by backend pagination & search)
  const filteredAllDevices = useMemo(() => {
    return enrichedAllDevices;
  }, [enrichedAllDevices]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredInitialSyncs = syncReport?.initialSyncs?.filter((sync) => {
    if (!initialSyncSearch.trim()) return true;
    const search = initialSyncSearch.toLowerCase();
    const userId = sync.userId?.toLowerCase() || "";
    const id = sync._id?.toLowerCase() || "";
    const companyId = sync.companyId?._id?.toLowerCase() || "";
    const companyName = sync.companyId?.tally_companyInfo?.Name?.toLowerCase() || "";
    const dbName = sync.dbName?.toLowerCase() || "";
    const deviceId = sync.deviceId?.toLowerCase() || "";

    return (
      userId.includes(search) ||
      id.includes(search) ||
      companyId.includes(search) ||
      companyName.includes(search) ||
      dbName.includes(search) ||
      deviceId.includes(search)
    );
  }) || [];

  const filteredMissedEvents = syncReport?.missedEvents?.filter((evt) => {
    if (!missedEventSearch.trim()) return true;
    const search = missedEventSearch.toLowerCase();
    const id = evt._id?.toLowerCase() || "";
    const deviceId = evt.deviceId?.toLowerCase() || "";
    const userId = evt.userId?.toLowerCase() || "";
    const eventType = evt.eventType?.toLowerCase() || "";
    const dbName = evt.payload?.dbName?.toLowerCase() || "";
    const companyName = evt.payload?.companyName?.toLowerCase() || "";
    const companyId = evt.payload?.companyId?.toLowerCase() || "";

    return (
      id.includes(search) ||
      deviceId.includes(search) ||
      userId.includes(search) ||
      eventType.includes(search) ||
      dbName.includes(search) ||
      companyName.includes(search) ||
      companyId.includes(search)
    );
  }) || [];

  const filteredIncrementalLogs = syncReport?.incrementalLogs?.filter((log) => {
    if (!incrementalLogSearch.trim()) return true;
    const search = incrementalLogSearch.toLowerCase();
    const id = log._id?.toLowerCase() || "";
    const deviceId = log.deviceId?.toLowerCase() || "";
    const companyName = log.companyName?.toLowerCase() || "";
    const dbName = log.dbName?.toLowerCase() || "";
    const lastVoucherAlterId = String(log.lastVoucherAlterId ?? "").toLowerCase();
    const lastMasterAlterId = String(log.lastMasterAlterId ?? "").toLowerCase();

    return (
      id.includes(search) ||
      deviceId.includes(search) ||
      companyName.includes(search) ||
      dbName.includes(search) ||
      lastVoucherAlterId.includes(search) ||
      lastMasterAlterId.includes(search)
    );
  }) || [];
  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        backgroundColor: "background.default",
        padding: "16px",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "5px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "divider",
          borderRadius: "4px",
        },
      }}
    >
      {/* Header Controls */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
          padding: "16px 20px",
          borderRadius: "12px",
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.5px" }}
          >
            Socket Connections
          </Typography>
          <Chip
            label={currentTab === "live" ? `${connections.length} Live` : `${allDevicesCount} Registered`}
            color={currentTab === "live" ? "success" : "primary"}
            variant="outlined"
            sx={{
              fontWeight: 700,
              fontSize: "13px",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexGrow: { xs: 1, md: 0 } }}>
          <TextField
            placeholder={currentTab === "live" ? "Search live connections..." : "Search all devices..."}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              if (currentTab === "all") {
                setAllDevicesPage(1);
              }
            }}
            size="small"
            sx={{
              width: { xs: "100%", sm: "280px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Refresh connections and devices">
            <IconButton
              onClick={() => handleRefreshAll(true, true)}
              disabled={loading}
              sx={{
                backgroundColor: theme.palette.mode === "light" ? "#f8f9fa" : "#1e293b",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "10px",
                color: "text.primary",
                "&:hover": {
                  backgroundColor: theme.palette.mode === "light" ? "#f1f5f9" : "#334155",
                  transform: "rotate(45deg)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Tabs switcher */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          padding: "0 16px",
          marginBottom: "16px",
        }}
      >
        <Tabs
          value={currentTab}
          onChange={(e, val) => {
            setCurrentTab(val);
            setSearchValue(""); // Clear search value when changing tab
          }}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              fontSize: "14px",
              minHeight: "48px",
            }
          }}
        >
          <Tab label={`Live (${connections.length})`} value="live" />
          <Tab label={`All (${allDevicesCount})`} value="all" />
        </Tabs>
      </Paper>

      {/* Main Grid View */}
      {loading ? (
        <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Spinner />
        </Box>
      ) : (currentTab === "live" ? filteredConnections : filteredAllDevices).length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "48px",
            borderRadius: "16px",
            backgroundColor: "background.paper",
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <DevicesIcon sx={{ fontSize: "64px", color: "text.secondary", marginBottom: "16px" }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary", marginBottom: "8px" }}>
            {currentTab === "live" ? "No Socket Connections Found" : "No Registered Devices Found"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
            {searchValue 
              ? "Try modifying your search criteria." 
              : (currentTab === "live" 
                  ? "There are currently no active devices connected via web sockets." 
                  : "There are no devices registered in the system.")}
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
            gap: "12px",
          }}
        >
          {(currentTab === "live" ? filteredConnections : filteredAllDevices).map((conn, index) => {
            const isOnline = !!conn.socketId;
            return (
              <Card
                key={conn.deviceInfo?._id || conn.socketId || index}
                onClick={() => handleCardClick(conn)}
                sx={{
                  borderRadius: "12px",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.02)",
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: theme.palette.mode === "light"
                      ? "0px 6px 15px rgba(68, 112, 173, 0.08)"
                      : "0px 6px 15px rgba(0, 0, 0, 0.4)",
                    borderColor: "primary.main",
                  },
                  position: "relative",
                  height: "215px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  padding: "12px",
                  backgroundColor: "background.paper",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "10px",
                    bottom: "10px",
                    width: "3px",
                    backgroundColor: isOnline ? "#4caf50" : "#94a3b8",
                    borderRadius: "0 2px 2px 0",
                  },
                }}
              >
                {/* Top Row: Device Name & Status */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                    <PhoneAndroidIcon sx={{ fontSize: "16px", color: isOnline ? "primary.main" : "text.secondary", flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: "12.5px",
                      }}
                    >
                      {conn.deviceInfo?.deviceName || "Unknown Device"}
                    </Typography>
                  </Box>
                  <Tooltip title={isOnline ? "Live Socket Connection" : "Device Offline"}>
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: isOnline ? "#4caf50" : "#94a3b8",
                        animation: isOnline ? "pulse 1.8s infinite" : "none",
                        flexShrink: 0,
                        ...(isOnline ? pulseAnimation : {}),
                      }}
                    />
                  </Tooltip>
                </Box>

                {/* User details */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <PersonIcon sx={{ fontSize: "13px", color: "text.secondary", flexShrink: 0 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: "11px",
                      }}
                    >
                      {conn.userInfo?.name || "N/A"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                    <EmailIcon sx={{ fontSize: "13px", color: "text.secondary", flexShrink: 0 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "10.5px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {conn.userInfo?.email || "N/A"}
                    </Typography>
                  </Box>
                </Box>

                {/* Device metadata and connection time */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "6px" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "10px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {conn.deviceInfo?.manufacturer || "Generic"} • {conn.deviceInfo?.modelName || "Model"}
                  </Typography>
                  <Tooltip title={conn.connectedAt ? moment(conn.connectedAt).format("DD MMM YYYY, h:mm:ss a") : "Offline"}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontSize: "10px", fontWeight: 550, flexShrink: 0 }}
                    >
                      {conn.connectedAt ? `${moment(conn.connectedAt).fromNow(true)} ago` : "Offline"}
                    </Typography>
                  </Tooltip>
                </Box>

                {/* Socket ID badge */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: theme.palette.mode === "light" ? "#f8f9fa" : "#1e293b50",
                    borderRadius: "6px",
                    padding: "2px 6px",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <VpnKeyIcon sx={{ fontSize: "11px", color: "text.secondary" }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "9px",
                        color: "text.secondary",
                        fontWeight: 600,
                      }}
                    >
                      {conn.socketId || "No active socket session"}
                    </Typography>
                  </Box>
                  {isOnline && (
                    <Tooltip title={copiedId === conn.socketId ? "Copied!" : "Copy"}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(conn.socketId);
                        }}
                        sx={{ padding: "0px" }}
                      >
                        {copiedId === conn.socketId ? (
                          <CheckIcon sx={{ fontSize: "12px", color: "success.main" }} />
                        ) : (
                          <ContentCopyIcon sx={{ fontSize: "11px", color: "text.secondary" }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {/* Device ID badge */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: theme.palette.mode === "light" ? "#f8f9fa" : "#1e293b50",
                    borderRadius: "6px",
                    padding: "2px 6px",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <Grid3x3Icon sx={{ fontSize: "11px", color: "text.secondary" }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "9px",
                        color: "text.secondary",
                        fontWeight: 600,
                      }}
                    >
                      {conn.deviceInfo?._id}
                    </Typography>
                  </Box>
                  <Tooltip title={copiedId === conn.deviceInfo?._id ? "Copied!" : "Copy"}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy(conn.deviceInfo?._id || "");
                      }}
                      sx={{ padding: "0px" }}
                    >
                      {copiedId === conn.deviceInfo?._id ? (
                        <CheckIcon sx={{ fontSize: "12px", color: "success.main" }} />
                      ) : (
                        <ContentCopyIcon sx={{ fontSize: "11px", color: "text.secondary" }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Manual Event Trigger Button */}
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!isOnline}
                  startIcon={<PlayArrowIcon sx={{ fontSize: "12px !important" }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenTriggerModal(conn);
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    fontWeight: 700,
                    height: "26px",
                    borderColor: isOnline ? "primary.main" : "action.disabledBackground",
                    color: isOnline ? "primary.main" : "text.disabled",
                    borderRadius: "6px",
                    width: "100%",
                    marginTop: "4px",
                    "&:hover": isOnline ? {
                      backgroundColor: "rgba(68, 112, 173, 0.05)",
                      borderColor: "primary.dark",
                    } : {},
                  }}
                >
                  Manual Event Trigger
                </Button>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Pagination for All Tab */}
      {currentTab === "all" && allDevicesTotalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: "24px",
            marginBottom: "16px",
          }}
        >
          <Pagination
            count={allDevicesTotalPages}
            page={allDevicesPage}
            onChange={(e, val) => {
              setAllDevicesPage(val);
              if (containerRef.current) {
                containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            color="primary"
            size="medium"
            siblingCount={1}
            boundaryCount={1}
            sx={{
              "& .MuiPaginationItem-root": {
                fontWeight: 600,
                borderRadius: "8px",
              },
            }}
          />
        </Box>
      )}

      {/* Sync Report Right Slide Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", md: "50%" },
            maxWidth: "700px",
            backgroundColor: theme.palette.mode === "light" ? "#f8f9fa" : "background.default",
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            borderLeft: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            padding: "20px 24px",
            backgroundColor: "primary.main",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Device Sync Report
            </Typography>
            {selectedConnection && (
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                {selectedConnection.deviceInfo?.deviceName} ({selectedConnection.userInfo?.name})
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tab Selection */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", backgroundColor: "background.paper" }}>
          <Tabs
            value={drawerTab}
            onChange={(e, val) => setDrawerTab(val)}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab
              label={`Initial Syncs (${syncReport?.initialSyncs?.length || 0})`}
              sx={{ fontWeight: 600, fontSize: "12px" }}
            />
            <Tab
              label={`Missed Events (${syncReport?.missedEvents?.length || 0})`}
              sx={{ fontWeight: 600, fontSize: "12px" }}
            />
            <Tab
              label={`Incremental Logs (${syncReport?.incrementalLogs?.length || 0})`}
              sx={{ fontWeight: 600, fontSize: "12px" }}
            />
          </Tabs>
        </Box>

        {/* Drawer Content */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {syncReportLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
              <Spinner />
            </Box>
          ) : !syncReport ? (
            <Box sx={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
              <WarningAmberIcon sx={{ fontSize: "48px", marginBottom: "8px" }} />
              <Typography variant="body1">No Sync Report Data Available</Typography>
            </Box>
          ) : (
            <>
              {/* TAB 0: INITIAL SYNCS */}
              {drawerTab === 0 && (
                <>
                  {syncReport.initialSyncs.length > 0 && (
                    <TextField
                      placeholder="Search initial syncs..."
                      value={initialSyncSearch}
                      onChange={(e) => setInitialSyncSearch(e.target.value)}
                      size="small"
                      fullWidth
                      sx={{
                        marginBottom: "16px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "background.paper",
                          "& fieldset": {
                            borderColor: "divider",
                          },
                          "&:hover fieldset": {
                            borderColor: "text.secondary",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "text.secondary", fontSize: "20px" }} />
                          </InputAdornment>
                        ),
                        endAdornment: initialSyncSearch && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setInitialSyncSearch("")}
                              sx={{ color: "text.secondary", padding: "4px" }}
                            >
                              <CloseIcon sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  {syncReport.initialSyncs.length === 0 ? (
                    <Box sx={{ textAlign: "center", padding: "30px 0", color: "text.secondary" }}>
                      <Typography variant="body2">No initial sync reports found for this device.</Typography>
                    </Box>
                  ) : filteredInitialSyncs.length === 0 ? (
                    <Box sx={{ textAlign: "center", padding: "30px 0", color: "text.secondary" }}>
                      <Typography variant="body2">No initial sync reports match your search criteria.</Typography>
                    </Box>
                  ) : (
                    filteredInitialSyncs.map((sync) => {
                      const isExpanded = !!expandedItems[sync._id];
                      const findVch = sync?.modules?.vouchers || null
                      return (
                        <Paper
                          key={sync._id}
                          elevation={0}
                          sx={{
                            borderRadius: "12px",
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            padding: "18px 20px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              borderColor: "primary.main",
                              boxShadow: theme.palette.mode === "light"
                                ? "0 4px 12px rgba(0, 0, 0, 0.05)"
                                : "0 4px 12px rgba(0, 0, 0, 0.3)",
                            },
                          }}
                        >
                          {/* Sync Main Info */}
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                                <BusinessIcon sx={{ fontSize: "18px", color: "primary.main" }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                                  {sync.companyId?.tally_companyInfo?.Name || "Unknown Company"}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                                DB Name: <strong>{sync.dbName}</strong>
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                                FY: {sync.fy?.start ? `${moment(sync.fy.start, "YYYYMMDD").format("YYYY")}-${moment(sync.fy.end, "YYYYMMDD").format("YYYY")}` : "N/A"}
                              </Typography>
                            </Box>
                            <Chip
                              label={sync.status || "completed"}
                              color={sync.status === "completed" ? "success" : "warning"}
                              size="small"
                              sx={{ fontWeight: 700, textTransform: "capitalize", fontSize: "11px", borderRadius: "6px" }}
                            />
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderTop: "1px dashed",
                              borderColor: "divider",
                              paddingTop: "10px",
                              marginTop: "4px"
                            }}
                          >
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 550 }}>
                              Start: {moment(sync.createdAt).format("DD MMM YYYY, h:mm a")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 550 }}>
                              End: {findVch && moment(findVch.completedAt).format("DD MMM YYYY, h:mm a")}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(sync._id);
                              }}
                              sx={{
                                color: "primary.main",
                                padding: "4px",
                                transition: "transform 0.2s",
                                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)"
                              }}
                            >
                              <ExpandMoreIcon />
                            </IconButton>
                          </Box>

                          {/* Expandable Module Wise Sync Record */}
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ marginTop: "12px", backgroundColor: theme.palette.mode === "light" ? "#f8f9fa" : "#1e293b50", borderRadius: "8px", padding: "14px", border: "1px solid", borderColor: "divider" }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", display: "block", marginBottom: "8px", letterSpacing: "0.5px" }}>
                                Module-wise Status
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "8px",
                                  maxHeight: "300px",
                                  overflowY: "auto",
                                  paddingRight: "6px",
                                  paddingBottom: "24px",
                                  "&::-webkit-scrollbar": {
                                    width: "4px",
                                  },
                                  "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: "divider",
                                    borderRadius: "4px",
                                  },
                                  "&::-webkit-scrollbar-track": {
                                    backgroundColor: "transparent",
                                  },
                                }}
                              >
                                {Object.keys(sync.modules || {}).map((modKey) => {
                                  const modData = sync.modules[modKey];
                                  const durationMs = moment(modData.completedAt).diff(moment(modData.startedAt));
                                  const durationSec = (durationMs / 1000).toFixed(2);
                                  return (
                                    <Box key={modKey} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "background.default", padding: "8px 12px", borderRadius: "6px", border: "1px solid", borderColor: "divider", flexShrink: 0 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary", textTransform: "capitalize" }}>
                                        {modKey.replace(/_/g, " ")}
                                      </Typography>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                          {durationSec}s
                                        </Typography>
                                        {modData.status ? (
                                          <CheckCircleIcon sx={{ fontSize: "16px", color: "success.main" }} />
                                        ) : (
                                          <CancelIcon sx={{ fontSize: "16px", color: "error.main" }} />
                                        )}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })
                  )}
                </>
              )}
              {/* TAB 1: MISSED EVENTS */}
              {drawerTab === 1 && (
                <>
                  {syncReport.missedEvents.length > 0 && (
                    <TextField
                      placeholder="Search missed events..."
                      value={missedEventSearch}
                      onChange={(e) => setMissedEventSearch(e.target.value)}
                      size="small"
                      fullWidth
                      sx={{
                        marginBottom: "16px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "background.paper",
                          "& fieldset": {
                            borderColor: "divider",
                          },
                          "&:hover fieldset": {
                            borderColor: "text.secondary",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "text.secondary", fontSize: "20px" }} />
                          </InputAdornment>
                        ),
                        endAdornment: missedEventSearch && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setMissedEventSearch("")}
                              sx={{ color: "text.secondary", padding: "4px" }}
                            >
                              <CloseIcon sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  {syncReport.missedEvents.length === 0 ? (
                    <Box sx={{ textAlign: "center", padding: "30px 0", color: "text.secondary" }}>
                      <Typography variant="body2">No missed events recorded for this device.</Typography>
                    </Box>
                  ) : filteredMissedEvents.length === 0 ? (
                    <Box sx={{ textAlign: "center", padding: "30px 0", color: "text.secondary" }}>
                      <Typography variant="body2">No missed events match your search criteria.</Typography>
                    </Box>
                  ) : (
                    filteredMissedEvents.map((evt) => {
                      const isExpanded = !!expandedItems[evt._id];
                      const accentColor = theme.palette.mode === "light" ? "#b27a00" : "#ffd54f";
                      return (
                        <Paper
                          key={evt._id}
                          onClick={() => toggleExpand(evt._id)}
                          elevation={0}
                          sx={{
                            borderRadius: "12px",
                            border: "1px solid",
                            borderColor: theme.palette.mode === "light" ? "#ffe082" : "#ffe08230",
                            backgroundColor: theme.palette.mode === "light" ? "#fffdf0" : "#ffe08208", // Soft light warm yellow background
                            cursor: "pointer",
                            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            padding: "18px 20px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            "&:hover": {
                              borderColor: "#ffb300",
                              boxShadow: theme.palette.mode === "light"
                                ? "0 4px 12px rgba(255, 179, 0, 0.12)"
                                : "0 4px 12px rgba(0, 0, 0, 0.4)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          {/* Header row */}
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                            <Box sx={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                              <BusinessIcon sx={{ fontSize: "18px", color: "text.secondary" }} />
                              <Chip
                                label={evt?.payload?.companyName || "No Company"}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "10px",
                                  backgroundColor: theme.palette.mode === "light" ? "#ddf9f8" : "#ddf9f820",
                                  color: theme.palette.mode === "light" ? "#006064" : "#80deea",
                                  borderRadius: "6px",
                                  letterSpacing: "0.5px"
                                }}
                              />
                              <Chip
                                label={evt.eventType}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "10px",
                                  backgroundColor: theme.palette.mode === "light" ? "#ffe082" : "#ffe08220",
                                  color: accentColor,
                                  borderRadius: "6px",
                                  letterSpacing: "0.5px"
                                }}
                              />
                              <Chip
                                label={evt.status}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: "capitalize",
                                  fontSize: "10px",
                                  borderColor: theme.palette.mode === "light" ? "#ffd54f" : "#ffd54f30",
                                  color: accentColor,
                                  borderRadius: "6px"
                                }}
                              />
                              <Chip
                                label={evt.isCompleted ? "Completed" : "Pending"}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: "capitalize",
                                  fontSize: "10px",
                                  borderColor: theme.palette.mode === "light" ? "#ffd54f" : "#ffd54f30",
                                  color: accentColor,
                                  borderRadius: "6px"
                                }}
                              />
                            </Box>
                            <IconButton
                              size="small"
                              sx={{
                                color: accentColor,
                                padding: "4px",
                                transition: "transform 0.2s",
                                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)"
                              }}
                            >
                              <ExpandMoreIcon />
                            </IconButton>
                          </Box>
 
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.primary",
                              fontWeight: 600,
                              fontSize: "13.5px",
                              lineHeight: "1.6",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {evt.payload?.message || "Incremental sync request triggered"}
                          </Typography>
 
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: "8px",
                              borderTop: "1px dashed",
                              borderColor: theme.palette.mode === "light" ? "rgba(255, 224, 130, 0.6)" : "rgba(255, 224, 130, 0.2)",
                              paddingTop: "10px",
                              marginTop: "4px"
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 500, color: "text.secondary" }}>
                              DB: <strong style={{ color: theme.palette.text.primary }}>{evt.payload?.dbName || "N/A"}</strong>
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                              Created : {moment(evt.createdAt).format("DD MMM YYYY, h:mm a")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                              Updated : {moment(evt.updatedAt).format("DD MMM YYYY, h:mm a")}
                            </Typography>
                          </Box>
 
                          {/* Collapse details */}
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              onClick={(e) => e.stopPropagation()} // Prevent collapse toggle when clicking on the payload text
                              sx={{
                                marginTop: "12px",
                                backgroundColor: "background.paper",
                                borderRadius: "8px",
                                padding: "14px",
                                border: "1px solid",
                                borderColor: "divider",
                                boxShadow: "inset 0 1px 3px rgba(0,0,0,0.03)"
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: accentColor,
                                  textTransform: "uppercase",
                                  display: "block",
                                  marginBottom: "8px",
                                  letterSpacing: "0.5px"
                                }}
                              >
                                Payload Details
                              </Typography>
                              <pre
                                style={{
                                  margin: 0,
                                  padding: "10px",
                                  fontSize: "11px",
                                  backgroundColor: theme.palette.mode === "light" ? "#fffdf0" : "#1e293b50",
                                  borderRadius: "6px",
                                  border: "1px solid",
                                  borderColor: theme.palette.mode === "light" ? "#fff59d" : "#ffd54f30",
                                  overflowX: "auto",
                                  fontFamily: "Consolas, Monaco, monospace",
                                  color: theme.palette.text.primary,
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-all"
                                }}
                              >
                                {JSON.stringify(evt.payload, null, 2)}
                              </pre>
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })
                  )}
                </>
              )}
              {/* TAB 2: INCREMENTAL LOGS */}
              {drawerTab === 2 && (
                <>
                  {syncReport.incrementalLogs.length > 0 && (
                    <TextField
                      placeholder="Search incremental logs..."
                      value={incrementalLogSearch}
                      onChange={(e) => setIncrementalLogSearch(e.target.value)}
                      size="small"
                      fullWidth
                      sx={{
                        marginBottom: "16px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "background.paper",
                          "& fieldset": {
                            borderColor: "divider",
                          },
                          "&:hover fieldset": {
                            borderColor: "text.secondary",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "text.secondary", fontSize: "20px" }} />
                          </InputAdornment>
                        ),
                        endAdornment: incrementalLogSearch && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setIncrementalLogSearch("")}
                              sx={{ color: "text.secondary", padding: "4px" }}
                            >
                              <CloseIcon sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  {syncReport.incrementalLogs.length === 0 ? (
                    <Box sx={{ textAlign: "center", padding: "30px 0", color: "text.secondary" }}>
                      <Typography variant="body2">No incremental logs found for this device.</Typography>
                    </Box>
                  ) : filteredIncrementalLogs.length === 0 ? (
                    <Box sx={{ textAlign: "center", padding: "30px 0", color: "text.secondary" }}>
                      <Typography variant="body2">No incremental logs match your search criteria.</Typography>
                    </Box>
                  ) : (
                    filteredIncrementalLogs.map((log) => {
                      const isExpanded = !!expandedItems[log._id];
                      const syncDurationMs = moment(log.syncEndTime || log.updatedAt).diff(moment(log.syncStartTime));
                      const syncDurationSec = (syncDurationMs / 1000).toFixed(1);
                      return (
                        <Paper
                          key={log._id}
                          elevation={0}
                          sx={{
                            borderRadius: "12px",
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            padding: "18px 20px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              borderColor: "primary.main",
                              boxShadow: theme.palette.mode === "light"
                                ? "0 4px 12px rgba(0, 0, 0, 0.05)"
                                : "0 4px 12px rgba(0, 0, 0, 0.3)",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Box sx={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                <BusinessIcon sx={{ fontSize: "18px", color: "text.secondary" }} />
                                <Chip
                                  label={log.companyName || "Unknown Company"}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "10px",
                                    backgroundColor: theme.palette.mode === "light" ? "#ddf9f8" : "#ddf9f820",
                                    color: theme.palette.mode === "light" ? "#006064" : "#80deea",
                                    borderRadius: "6px",
                                    letterSpacing: "0.5px"
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                                DB: <strong>{log.dbName}</strong>
                              </Typography>
                            </Box>
                            <Chip
                              label={log.status || "success"}
                              color={log.status === "success" ? "success" : "error"}
                              size="small"
                              sx={{ fontWeight: 700, fontSize: "10px", borderRadius: "6px" }}
                            />
                          </Box>
 
                          <Box sx={{ backgroundColor: theme.palette.mode === "light" ? "#f8f9fa" : "#1e293b50", borderRadius: "8px", padding: "8px 12px", display: "flex", justifyContent: "space-between", border: "1px solid", borderColor: "divider" }}>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Alter IDs: <strong>M:{log.lastMasterAlterId || 0} / V:{log.lastVoucherAlterId || 0}</strong>
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Duration: <strong>{syncDurationSec}s</strong>
                            </Typography>
                          </Box>
 
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderTop: "1px dashed",
                              borderColor: "divider",
                              paddingTop: "10px",
                              marginTop: "4px"
                            }}
                          >
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 550 }}>
                              Start: {moment(log.syncStartTime).format("DD MMM YYYY, h:mm a")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 550 }}>
                              End: {moment(log.syncEndTime).format("DD MMM YYYY, h:mm a")}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600 }}>
                                {Object.keys(log.moduleWiseSyncRecord || {}).length} modules
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(log._id);
                                }}
                                sx={{
                                  color: "primary.main",
                                  padding: "2px",
                                  transition: "transform 0.2s",
                                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)"
                                }}
                              >
                                <ExpandMoreIcon />
                              </IconButton>
                            </Box>
                          </Box>
 
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ marginTop: "12px", backgroundColor: theme.palette.mode === "light" ? "#f8f9fa" : "#1e293b50", borderRadius: "8px", padding: "14px", border: "1px solid", borderColor: "divider" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "8px",
                                  maxHeight: "250px",
                                  overflowY: "auto",
                                  paddingRight: "4px",
                                  paddingBottom: "24px",
                                  "&::-webkit-scrollbar": {
                                    width: "4px",
                                  },
                                  "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: "divider",
                                    borderRadius: "4px",
                                  },
                                  "&::-webkit-scrollbar-track": {
                                    backgroundColor: "transparent",
                                  },
                                }}
                              >
                                {Object.keys(log.moduleWiseSyncRecord || {}).map((modKey) => {
                                  const modData = log.moduleWiseSyncRecord[modKey];
                                  const durationMs = moment(modData.completedAt).diff(moment(modData.startedAt));
                                  const durationSec = (durationMs / 1000).toFixed(1);
                                  return (
                                    <Box key={modKey} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "background.default", padding: "6px 10px", borderRadius: "6px", border: "1px solid", borderColor: "divider", flexShrink: 0 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary", textTransform: "capitalize", fontSize: "10.5px" }}>
                                        {modKey}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "10px" }}>
                                        {durationSec}s {modData.isFullSyncNeeded ? "(Full)" : "(Incr)"}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Drawer>

      {/* Manual Event Trigger Dialog/Modal */}
      <Dialog
        open={triggerModalOpen}
        onClose={() => !triggerLoading && setTriggerModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
            backgroundColor: "background.default",
            overflow: "hidden",
          },
        }}
      >
        {/* Swagger-styled Header */}
        <Box
          sx={{
            backgroundColor: "#0f172a", // Keep dark slate header for CLI Swagger style
            color: "white",
            padding: "20px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #1e293b",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <TerminalIcon sx={{ color: "#38bdf8", fontSize: "24px" }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "16px", letterSpacing: "0.5px" }}>
                Developer Event Trigger Console
              </Typography>
              <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>
                Simulate socket notifications to device client
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setTriggerModalOpen(false)}
            disabled={triggerLoading}
            sx={{ color: "#94a3b8", "&:hover": { color: "white" } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ padding: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Target URL details */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: theme.palette.mode === "light" ? "#f1f5f9" : "#1e293b50",
              padding: "10px 14px",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Chip
              label="POST"
              sx={{
                backgroundColor: "#10b981", // green method tag
                color: "white",
                fontWeight: 700,
                fontSize: "11px",
                height: "24px",
                borderRadius: "4px",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                color: "text.primary",
                fontWeight: 600,
                fontSize: "11.5px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {accosyncIpConfig}/v2/internal/event/trigger
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Left Column: Config */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {/* Target Device Info */}
                <Box
                  sx={{
                    backgroundColor: "background.paper",
                    padding: "16px",
                    borderRadius: "10px",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>
                    Target Connection Info
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>Device Name:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary" }}>{triggerConnection?.deviceInfo?.deviceName || "Unknown"}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>Socket ID:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary", fontFamily: "monospace" }}>{triggerConnection?.socketId}</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Event Dropdown Selector */}
                <FormControl fullWidth size="small">
                  <InputLabel id="event-select-label" sx={{ fontWeight: 600, fontSize: "13px" }}>Event Name</InputLabel>
                  <Select
                    labelId="event-select-label"
                    value={selectedEvent}
                    label="Event Name"
                    onChange={(e) => handleEventChange(e.target.value as string)}
                    sx={{
                      borderRadius: "8px",
                      backgroundColor: "background.paper",
                      "& .MuiOutlinedInput-input": {
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "text.primary"
                      }
                    }}
                  >
                    {[
                      "INCREMENTAL_SYNC_REQUEST",
                      "REFRESH_COMPANY_LIST",
                      "COMPANY_DELETED",
                      "PERMISSION_CHANGED",
                      "PERMISSION_CHANGED_NEED_RESYNC",
                      "PERMISSION_CHANGED_REPORT_SYNC",
                      "DEVICE_REMOVED",
                      "LOGOUT_REQUEST",
                      "USER_DELETED",
                      "USER_INVITE",
                      "LICENSE_TRANSFERRED",
                      "REFRESH_APP_CONFIG",
                      "REFRESH_INVOICE_CONFIG",
                      "OTHER",
                    ].map((evtName) => (
                      <MenuItem key={evtName} value={evtName} sx={{ fontSize: "13px", fontWeight: 550 }}>
                        {evtName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            {/* Right Column: Editable Payload Editor */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", display: "flex", justifyContent: "space-between" }}>
                  <span>Payload JSON Body</span>
                  {jsonError ? (
                    <span style={{ color: "#ef4444", textTransform: "none" }}>✖ Invalid JSON</span>
                  ) : (
                    <span style={{ color: "#10b981", textTransform: "none" }}>✔ Valid JSON</span>
                  )}
                </Typography>
                <TextField
                  multiline
                  rows={6}
                  value={payloadJson}
                  onChange={(e) => handlePayloadChange(e.target.value)}
                  error={!!jsonError}
                  helperText={jsonError}
                  FormHelperTextProps={{
                    sx: {
                      fontFamily: "monospace",
                      fontSize: "10px",
                      margin: "4px 0 0 0",
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "monospace",
                      fontSize: "12.5px",
                      backgroundColor: "background.paper",
                      borderRadius: "10px",
                      lineHeight: "1.5",
                      "& fieldset": {
                        borderColor: jsonError ? "error.main" : "divider",
                      }
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Action Row */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px", borderTop: "1px solid", borderColor: "divider", paddingTop: "20px" }}>
            <Button
              variant="outlined"
              onClick={() => setTriggerModalOpen(false)}
              disabled={triggerLoading}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                borderColor: "divider",
                color: "text.primary",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "text.secondary",
                  backgroundColor: "transparent"
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSendTrigger}
              disabled={triggerLoading || !!jsonError}
              endIcon={<SendIcon sx={{ fontSize: "14px" }} />}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: "#10b981", // Execute Green
                fontWeight: 700,
                boxShadow: "0 4px 6px -1px rgb(16 185 129 / 0.1), 0 2px 4px -2px rgb(16 185 129 / 0.1)",
                "&:hover": {
                  backgroundColor: "#059669",
                  boxShadow: "0 10px 15px -3px rgb(16 185 129 / 0.2)"
                }
              }}
            >
              {triggerLoading ? "Sending..." : "Execute"}
            </Button>
          </Box>

          {/* Response Swagger Terminal Section */}
          {(triggerResponse !== null || triggerLoading) && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "12px" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                Server Response Console
              </Typography>

              {/* Terminal Frame */}
              <Box
                sx={{
                  backgroundColor: "#0f172a", // Dark slate background
                  borderRadius: "12px",
                  border: "1px solid #334155",
                  overflow: "hidden",
                  boxShadow: "inset 0 2px 4px rgba(0, 0, 0, 0.4)",
                }}
              >
                {/* Terminal Header Bar */}
                <Box
                  sx={{
                    backgroundColor: "#1e293b",
                    padding: "10px 16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #334155",
                  }}
                >
                  <Box sx={{ display: "flex", gap: "6px" }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#ef4444" }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#eab308" }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#22c55e" }} />
                  </Box>

                  {triggerResponseStatus !== null && (
                    <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
                      <Chip
                        label={`STATUS: ${triggerResponseStatus}`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "9px",
                          backgroundColor: triggerResponseStatus >= 200 && triggerResponseStatus < 300 ? "rgba(34, 197, 94, 0.15)" : "rgba(239, 68, 68, 0.15)",
                          color: triggerResponseStatus >= 200 && triggerResponseStatus < 300 ? "#4ade80" : "#f87171",
                          borderRadius: "4px",
                          border: triggerResponseStatus >= 200 && triggerResponseStatus < 300 ? "1px solid #22c55e" : "1px solid #ef4444",
                        }}
                      />
                      {triggerResponseTime !== null && (
                        <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: "monospace", fontSize: "10.5px" }}>
                          TIME: {triggerResponseTime} ms
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Terminal Content Body */}
                <Box sx={{ padding: "16px", minHeight: "100px", maxHeight: "250px", overflowY: "auto" }}>
                  {triggerLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", color: "#38bdf8", fontFamily: "monospace", fontSize: "12px" }}>
                      <Box
                        sx={{
                          width: "12px",
                          height: "12px",
                          border: "2px solid #38bdf8",
                          borderTop: "2px solid transparent",
                          borderRadius: "50%",
                          animation: "spin 1s infinite linear",
                          "@keyframes spin": {
                            "0%": {
                              transform: "rotate(0deg)",
                            },
                            "100%": {
                              transform: "rotate(360deg)",
                            },
                          },
                        }}
                      />
                      <span>Sending POST payload to device socket channels...</span>
                    </Box>
                  ) : (
                    <pre
                      style={{
                        margin: 0,
                        fontFamily: "Fira Code, Consolas, Monaco, monospace",
                        fontSize: "12px",
                        color: triggerResponseStatus && triggerResponseStatus >= 200 && triggerResponseStatus < 300 ? "#4ade80" : "#f87171",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                      }}
                    >
                      {JSON.stringify(triggerResponse, null, 2)}
                    </pre>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
